import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Users, FileText, CheckSquare, GitBranch,
  Clock, TrendingUp, AlertTriangle, Plus, Search,
  Video, Building2, Mic, Activity, BarChart3,
  ArrowRight, PlayCircle, Filter, RefreshCw, CalendarDays
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MeetingIntelligenceDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      setRefreshing(true);
      const res = await axios.get(`${API}/api/meeting-intelligence/dashboard?token=${token}`);
      setDashboard(res.data.dashboard);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const stats = {
    meetings: dashboard?.meetings || { total: 0, this_week: 0, today: 0, by_type: {} },
    tasks: dashboard?.tasks || { total: 0, pending: 0, overdue: 0, completed_this_week: 0 },
    decisions: dashboard?.decisions || { total: 0, this_month: 0 },
    recent_meetings: dashboard?.recent_meetings || [],
    upcoming_meetings: dashboard?.upcoming_meetings || [],
    my_tasks: dashboard?.my_tasks || []
  };

  return (
    <div className="p-6 space-y-6" data-testid="meeting-intelligence-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Activity className="h-7 w-7 text-blue-600" />
            Meeting Intelligence
          </h1>
          <p className="text-slate-500 mt-1">Enterprise knowledge repository and meeting management</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchDashboard} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => navigate('/meeting-intelligence/meetings/new')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Meeting
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/meeting-intelligence/meetings')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Meetings</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.meetings.total}</p>
                <p className="text-xs text-blue-600 mt-1">{stats.meetings.this_week} this week</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/meeting-intelligence/tasks')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Tasks</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.tasks.pending}</p>
                <p className="text-xs text-red-600 mt-1">{stats.tasks.overdue} overdue</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <CheckSquare className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/meeting-intelligence/decisions')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Decisions</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.decisions.total}</p>
                <p className="text-xs text-emerald-600 mt-1">{stats.decisions.this_month} this month</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <GitBranch className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate('/meeting-intelligence/repository')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Repository Items</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white">
                  {stats.meetings.total + stats.tasks.total + stats.decisions.total}
                </p>
                <p className="text-xs text-purple-600 mt-1">Searchable records</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:bg-blue-50 hover:border-blue-300"
              onClick={() => navigate('/meeting-intelligence/meetings/new?type=physical')}
            >
              <Building2 className="h-6 w-6 text-blue-600" />
              <span className="text-sm">Physical Meeting</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:bg-purple-50 hover:border-purple-300"
              onClick={() => navigate('/meeting-intelligence/meetings/new?type=teams')}
            >
              <Video className="h-6 w-6 text-purple-600" />
              <span className="text-sm">Teams Meeting</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:bg-cyan-50 hover:border-cyan-300"
              onClick={() => navigate('/meeting-intelligence/templates')}
            >
              <FileText className="h-6 w-6 text-cyan-600" />
              <span className="text-sm">Templates</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:bg-indigo-50 hover:border-indigo-300"
              onClick={() => navigate('/meeting-intelligence/calendar')}
            >
              <CalendarDays className="h-6 w-6 text-indigo-600" />
              <span className="text-sm">Calendar View</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:bg-rose-50 hover:border-rose-300"
              onClick={() => navigate('/meeting-intelligence/analytics')}
            >
              <BarChart3 className="h-6 w-6 text-rose-600" />
              <span className="text-sm">Analytics</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:bg-amber-50 hover:border-amber-300"
              onClick={() => navigate('/meeting-intelligence/tasks/board')}
            >
              <CheckSquare className="h-6 w-6 text-amber-600" />
              <span className="text-sm">Task Board</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-24 flex-col gap-2 hover:bg-emerald-50 hover:border-emerald-300"
              onClick={() => navigate('/meeting-intelligence/repository')}
            >
              <Search className="h-6 w-6 text-emerald-600" />
              <span className="text-sm">Repository</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Meetings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Upcoming Meetings
              </CardTitle>
              <CardDescription>Scheduled for the next 7 days</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/meeting-intelligence/meetings')}>
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {stats.upcoming_meetings.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No upcoming meetings</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => navigate('/meeting-intelligence/meetings/new')}
                >
                  Schedule one now
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.upcoming_meetings.map((meeting) => (
                  <div 
                    key={meeting.id}
                    className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                    onClick={() => navigate(`/meeting-intelligence/meetings/${meeting.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          meeting.meeting_type === 'teams' ? 'bg-purple-100 dark:bg-purple-900/30' :
                          meeting.meeting_type === 'hybrid' ? 'bg-amber-100 dark:bg-amber-900/30' :
                          'bg-blue-100 dark:bg-blue-900/30'
                        }`}>
                          {meeting.meeting_type === 'teams' ? (
                            <Video className="h-5 w-5 text-purple-600" />
                          ) : meeting.meeting_type === 'hybrid' ? (
                            <Users className="h-5 w-5 text-amber-600" />
                          ) : (
                            <Building2 className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{meeting.title}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(meeting.scheduled_start).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="capitalize">{meeting.meeting_type}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Tasks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-amber-600" />
                My Tasks
              </CardTitle>
              <CardDescription>Action items assigned to you</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/meeting-intelligence/tasks')}>
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {stats.my_tasks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No pending tasks</p>
                <p className="text-sm mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.my_tasks.map((task) => (
                  <div 
                    key={task.id}
                    className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                    onClick={() => navigate(`/meeting-intelligence/tasks/${task.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white truncate">{task.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={
                            task.priority === 'high' ? 'destructive' :
                            task.priority === 'medium' ? 'default' : 'secondary'
                          } className="text-xs">
                            {task.priority}
                          </Badge>
                          {task.due_date && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className="ml-2 capitalize">{task.status?.replace('_', ' ')}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Meetings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-600" />
              Recent Meetings
            </CardTitle>
            <CardDescription>Latest meetings and their status</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {stats.recent_meetings.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No recent meetings</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-sm font-medium text-slate-500">Meeting</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-500">Type</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-500">Date</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-500">Status</th>
                    <th className="text-right p-3 text-sm font-medium text-slate-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_meetings.map((meeting) => (
                    <tr key={meeting.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="p-3">
                        <p className="font-medium text-slate-900 dark:text-white">{meeting.title}</p>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className="capitalize">{meeting.meeting_type}</Badge>
                      </td>
                      <td className="p-3 text-sm text-slate-500">
                        {meeting.scheduled_start ? new Date(meeting.scheduled_start).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-3">
                        <Badge variant={
                          meeting.status === 'completed' ? 'default' :
                          meeting.status === 'in_progress' ? 'secondary' :
                          meeting.status === 'cancelled' ? 'destructive' : 'outline'
                        } className="capitalize">
                          {meeting.status?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="p-3 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/meeting-intelligence/meetings/${meeting.id}`)}
                        >
                          View <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Meeting Types Distribution */}
      {stats.meetings?.by_type && Object.keys(stats.meetings.by_type).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-slate-600" />
              Meeting Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {Object.entries(stats.meetings.by_type).map(([type, count]) => (
                <div key={type} className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded ${
                    type === 'teams' ? 'bg-purple-500' :
                    type === 'hybrid' ? 'bg-amber-500' : 'bg-blue-500'
                  }`}></div>
                  <span className="text-sm text-slate-600 capitalize">{type}: <strong>{count}</strong></span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company Analytics Section */}
      <CompanyAnalytics token={token} />
    </div>
  );
};

// Company Analytics Component
const CompanyAnalytics = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const res = await axios.get(`${API}/api/meeting-intelligence/dashboard/company?token=${token}`);
        setData(res.data.company_dashboard);
      } catch (err) {
        console.error('Error fetching company analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyData();
  }, [token]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-600" />
          Company Analytics
        </CardTitle>
        <CardDescription>Organization-wide meeting insights</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Overview Stats */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-700">Overview</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Total Meetings</span>
                <span className="font-bold text-blue-600">{data.overview.total_meetings}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Total Tasks</span>
                <span className="font-bold text-amber-600">{data.overview.total_tasks}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Total Decisions</span>
                <span className="font-bold text-emerald-600">{data.overview.total_decisions}</span>
              </div>
            </div>
          </div>

          {/* Department Breakdown */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-700">By Department</h4>
            <div className="space-y-2">
              {data.by_department.slice(0, 5).map((dept, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">{dept.name}</span>
                      <span className="font-medium">{dept.meetings}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                        style={{ width: `${Math.min((dept.meetings / (data.overview.total_meetings || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="space-y-4">
            <h4 className="font-medium text-slate-700">Monthly Trend</h4>
            <div className="space-y-2">
              {data.monthly_trend.slice(-5).map((month, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-sm text-slate-500 w-20">{month.month}</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                      style={{ width: `${Math.min((month.meetings / Math.max(...data.monthly_trend.map(m => m.meetings), 1)) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-8 text-right">{month.meetings}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Meeting Type Distribution */}
        {data.by_type && Object.keys(data.by_type).length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium text-slate-700 mb-4">Meeting Types</h4>
            <div className="flex items-center gap-8">
              {Object.entries(data.by_type).map(([type, count]) => (
                <div key={type} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    type === 'teams' ? 'bg-purple-500' :
                    type === 'hybrid' ? 'bg-amber-500' : 'bg-blue-500'
                  }`} />
                  <span className="text-sm text-slate-600 capitalize">{type}</span>
                  <span className="text-sm font-bold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MeetingIntelligenceDashboard;
